/* storybook-check-ignore */
import { useEffect, useMemo, useRef, useState } from 'react';

import SwiperClass from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/virtual';

import some from 'lodash/some';
import styled from '@emotion/styled';
import { css, Global } from '@emotion/react';
import { Modal } from '@opendoor/bricks/complex';
import { Button, Icon, Box, Image, Text } from '@opendoor/bricks/core';
import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { ListingPhoto } from 'declarations/exclusives/listing';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { exclusives } from '@opendoor/bricks/theme';

interface PhotoViewActionOptions {
  text: string;
  ariaLabel: string;
  onClick: () => void;
  analyticsName: string;
  shouldHide?: boolean;
}

interface PhotoViewHeaderOptions {
  title: string;
  subtitle: string | undefined;
}

interface PhotoViewerProps {
  open: boolean;
  photos: ListingPhoto[];
  onClose: () => void;
  initialIndex?: number;
  header: PhotoViewHeaderOptions;
  ctaOptions?: PhotoViewActionOptions;
  onActiveIndexChanged?: (index: number) => void;
}

function PhotoViewer({
  open,
  header,
  photos,
  onClose,
  ctaOptions,
  initialIndex,
  onActiveIndexChanged,
}: PhotoViewerProps) {
  const swiper = useRef<SwiperClass>();
  const { isTablet, isDesktop } = useDevice();
  const [currentPhotoIdx, setCurrentPhotoIdx] = useState(initialIndex);

  // Only used when on mobile devices
  const [showCarousel, setShowCarousel] = useState(false);

  useEffect(() => {
    if (currentPhotoIdx === undefined) return;
    onActiveIndexChanged?.(currentPhotoIdx);
  }, [currentPhotoIdx, onActiveIndexChanged]);

  const initSwiper = (swiperItem: SwiperClass) => {
    swiper.current = swiperItem;
    setCurrentPhotoIdx(currentPhotoIdx ?? initialIndex);
  };

  const photosAgg = useMemo(() => {
    return photos.reduce<ListingPhoto[][]>((acc, _, idx, arr) => {
      if (idx % 3 === 2) return acc;

      if (idx % 3 === 0) {
        acc.push(arr.slice(idx, idx + 1));
      } else if (idx === arr.length - 1) {
        acc[acc.length - 1].push(arr[idx]);
      } else {
        acc.push(arr.slice(idx, idx + 2));
      }

      return acc;
    }, []);
  }, [photos]);

  useEffect(() => {
    if (!photos.length) return;
    if (currentPhotoIdx === undefined) return;

    const photo = photos[currentPhotoIdx];
    const photoElement = document.getElementById(`photo-${photo.id}`);

    photoElement?.scrollIntoView({ behavior: 'smooth' });
  }, [photos, currentPhotoIdx]);

  useEffect(() => {
    if (!swiper.current) return;
    if (currentPhotoIdx === undefined) return;
    if (currentPhotoIdx === swiper.current.realIndex) return;

    swiper.current.slideTo(currentPhotoIdx);
  }, [currentPhotoIdx]);

  // Keyboard Support
  useEffect(() => {
    function handleKeyDown(ev: KeyboardEvent) {
      switch (ev.code) {
        case 'ArrowUp':
        case 'ArrowLeft':
          setCurrentPhotoIdx((prev) => {
            const next = (prev ?? 0) - 1;
            return next < 0 ? photos.length - 1 : next;
          });
          break;
        case 'ArrowDown':
        case 'ArrowRight':
          setCurrentPhotoIdx((prev) => {
            const next = (prev ?? 0) + 1;
            return next >= photos.length ? 0 : next;
          });
          break;
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setCurrentPhotoIdx]);

  const nextSlide = () =>
    setCurrentPhotoIdx((prev) => {
      const next = (prev ?? 0) + 1;
      return next >= photos.length ? 0 : next;
    });

  const previousSlide = () =>
    setCurrentPhotoIdx((prev) => {
      const next = (prev ?? 0) - 1;
      return next < 0 ? photos.length - 1 : next;
    });

  const onHide = () => {
    setShowCarousel(false);
    swiper.current = undefined;
    onClose();
  };

  const priorPhotoDisclaimer = photos[0]?.isPrior ? (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="center"
      position="absolute"
      top={[5, null, 7]}
      width="100%"
      zIndex={1}
    >
      <Text
        bgColor="neutrals0"
        borderRadius="roundedSquare"
        boxShadow="2px 2px 7px 3px rgba(0,0,0,0.16)"
        px={6}
        py={5}
        size="s0"
      >
        Photos are from a historical listing in {photos[0].year}
      </Text>
    </Box>
  ) : null;

  // Desktop
  if (some([isDesktop, isTablet])) {
    return (
      <>
        {open && <Global styles={hideFrontApp} />}
        <Modal
          isOpen={open}
          analyticsName="cosmos-exclusives-photo-viewer-modal"
          hasPadding={false}
          onRequestClose={onHide}
          desktopAndTabletSize="fullScreen"
          headerProps={{
            ...header,
            rightComponent: ctaOptions && !ctaOptions.shouldHide && (
              <Button
                width="auto"
                height="44px"
                whiteSpace="nowrap"
                fontWeight="semibold"
                onClick={ctaOptions.onClick}
                aria-label={ctaOptions.ariaLabel}
                analyticsName={ctaOptions.analyticsName}
              >
                {ctaOptions.text}
              </Button>
            ),
            leftComponent: (
              <Button
                variant="icon"
                analyticsName="cosmos-exclusives-photo-viewer-close"
                onClick={onHide}
                id="modal-close-button"
                aria-label="Close modal"
              >
                <Icon name="close" size={16} />
              </Button>
            ),
          }}
        >
          <ThemedLite theme={exclusives}>
            <Box display="flex" height="100%">
              <Box
                width="25%"
                height="100%"
                display="flex"
                paddingLeft="7"
                paddingRight="4"
                overflow="scroll"
                flexDirection="column"
              >
                {photosAgg.map((photosGroup) => (
                  <Box
                    display="flex"
                    flexDirection="row"
                    key={photosGroup.map((it) => it.id).join('-')}
                  >
                    {photosGroup.map((photo) => {
                      const photoIdx = photos.findIndex((it) => it.id === photo.id);

                      return (
                        <Box
                          key={photo.id}
                          padding="1"
                          id={`photo-${photo.id}`}
                          width={`${100 / photosGroup.length}%`}
                        >
                          <Thumbnail
                            alt="thumbnail"
                            src={photo.thumbnail_urls.medium}
                            selected={currentPhotoIdx === photoIdx}
                            onClick={() => setCurrentPhotoIdx(photoIdx)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                ))}
              </Box>

              <Box width="75%" height="100%" display="flex" marginRight="7" position="relative">
                <Swiper
                  slidesPerView={1}
                  onSwiper={initSwiper}
                  initialSlide={currentPhotoIdx}
                  onSlideChange={(it) => setCurrentPhotoIdx(it.realIndex)}
                >
                  {photos.map((photo, idx) => (
                    <SwiperSlide key={photo.id} virtualIndex={idx}>
                      <SwiperImage alt="photo" src={photo.thumbnail_urls.medium} />
                    </SwiperSlide>
                  ))}
                </Swiper>
                {priorPhotoDisclaimer}
                <Box
                  zIndex={1}
                  width="100%"
                  bottom="40px"
                  display="flex"
                  position="absolute"
                  alignItems="center"
                  justifyContent="center"
                >
                  <NavBarActions
                    padding="1"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="rounded"
                  >
                    <Button
                      variant="icon"
                      padding="2"
                      width="auto"
                      height="auto"
                      aria-label=""
                      display="flex"
                      alignItems="center"
                      onClick={previousSlide}
                      analyticsName="cosmos-exclusives-photo-viewer-previous-photo"
                    >
                      <Icon name="chevron-left" size={16} />
                    </Button>
                    <span>
                      {(currentPhotoIdx ?? 0) + 1} of {photos.length}
                    </span>
                    <Button
                      padding="2"
                      width="auto"
                      height="auto"
                      aria-label=""
                      variant="icon"
                      display="flex"
                      alignItems="center"
                      onClick={nextSlide}
                      analyticsName="cosmos-exclusives-photo-viewer-next-photo"
                    >
                      <Icon name="chevron-right" size={16} />
                    </Button>
                  </NavBarActions>
                </Box>
              </Box>
            </Box>
          </ThemedLite>
        </Modal>
      </>
    );
  }

  // Mobile fallback
  return (
    <>
      {open && <Global styles={hideFrontApp} />}

      <Modal
        isOpen={open}
        analyticsName="cosmos-exclusives-photo-viewer-modal"
        hasPadding={false}
        onRequestClose={onHide}
        desktopAndTabletSize="fullScreen"
        headerProps={{
          ...header,
          leftComponent: showCarousel && (
            <Button
              variant="icon"
              id="modal-show-thumbnails"
              aria-label="View thumbnails"
              onClick={() => setShowCarousel(false)}
              analyticsName="cosmos-exclusives-photo-viewer-hide-carousel"
            >
              <Icon name="chevron-left" size={16} />
            </Button>
          ),
          rightComponent: (
            <Button
              variant="icon"
              onClick={onHide}
              id="modal-close-button"
              aria-label="Close modal"
              analyticsName="cosmos-exclusives-photo-viewer-close"
            >
              <Icon name="close" size={16} />
            </Button>
          ),
        }}
      >
        <ThemedLite theme={exclusives}>
          <SlideInContainer>
            <Box
              width="100%"
              height="100%"
              display="flex"
              overflow="scroll"
              flexDirection="column"
              position="relative"
            >
              {photosAgg.map((photosGroup) => (
                <Box
                  width="100%"
                  display="flex"
                  flexDirection="row"
                  key={photosGroup.map((it) => it.id).join('-')}
                >
                  {photosGroup.map((photo) => {
                    const photoIdx = photos.findIndex((it) => it.id === photo.id);

                    return (
                      <Box
                        key={photo.id}
                        padding="1"
                        id={`photo-${photo.id}`}
                        width={`${100 / photosGroup.length}%`}
                      >
                        <Thumbnail
                          alt="thumbnail"
                          src={photo.thumbnail_urls.medium}
                          selected={currentPhotoIdx === photoIdx}
                          onClick={() => {
                            setShowCarousel(true);
                            setCurrentPhotoIdx(photoIdx);
                          }}
                        />
                      </Box>
                    );
                  })}
                </Box>
              ))}

              {priorPhotoDisclaimer}

              {ctaOptions && !ctaOptions?.shouldHide && (
                <Box
                  bottom="22px"
                  width="100%"
                  display="flex"
                  position="fixed"
                  justifyContent="center"
                >
                  <Box
                    width="80%"
                    boxShadow="rgba(50, 50, 93, 0.25) 0px 50px 100px -20px, rgba(0, 0, 0, 0.3) 0px 30px 60px -30px;"
                  >
                    <Button
                      height="44px"
                      whiteSpace="nowrap"
                      fontWeight="semibold"
                      onClick={ctaOptions.onClick}
                      aria-label={ctaOptions.ariaLabel}
                      analyticsName={ctaOptions.analyticsName}
                    >
                      {ctaOptions.text}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            <Slide active={showCarousel}>
              <Box
                width="100%"
                height="100%"
                display="flex"
                position="relative"
                alignItems="center"
                justifyContent="center"
              >
                <Swiper
                  slidesPerView={1}
                  onSwiper={initSwiper}
                  initialSlide={currentPhotoIdx}
                  onSlideChange={(it) => setCurrentPhotoIdx(it.realIndex)}
                >
                  {photos.map((photo, idx) => (
                    <SwiperSlide key={photo.id} virtualIndex={idx}>
                      <SwiperImage alt="photo" src={photo.thumbnail_urls.medium} />
                    </SwiperSlide>
                  ))}
                </Swiper>

                <Box
                  zIndex={2}
                  width="100%"
                  bottom="40px"
                  display="flex"
                  position="absolute"
                  alignItems="center"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <NavBarActions
                    padding="1"
                    display="flex"
                    alignItems="center"
                    borderRadius="rounded"
                    justifyContent="center"
                  >
                    <Button
                      variant="icon"
                      padding="2"
                      width="auto"
                      height="auto"
                      aria-label=""
                      display="flex"
                      analyticsName="cosmos-exclusives-photo-viewer-previous-photo"
                      alignItems="center"
                      onClick={previousSlide}
                    >
                      <Icon name="chevron-left" size={16} />
                    </Button>
                    <span>
                      {(currentPhotoIdx ?? 0) + 1} of {photos.length}
                    </span>
                    <Button
                      variant="icon"
                      padding="2"
                      width="auto"
                      height="auto"
                      aria-label=""
                      display="flex"
                      analyticsName="cosmos-exclusives-photo-viewer-next-photo"
                      alignItems="center"
                      onClick={nextSlide}
                    >
                      <Icon name="chevron-right" size={16} />
                    </Button>
                  </NavBarActions>
                </Box>
              </Box>
            </Slide>
          </SlideInContainer>
        </ThemedLite>
      </Modal>
    </>
  );
}

const NavBarActions = styled(Box)`
  gap: 24px;
  color: var(--colors-neutrals0);
  background-color: rgba(0, 0, 0, 0.35);
`;

const SwiperImage = styled(Image)`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
`;

const Thumbnail = styled(Image)<{ selected: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;

  ${({ selected }) =>
    selected &&
    css`
      opacity: 0.5;
      border: 2px solid black;
    `}
`;

const hideFrontApp = css`
  #front-chat-holder {
    // !important is required due to how the FrontApp widget injects css
    display: none !important;
  }
`;

const SlideInContainer = styled(Box)`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
`;

const Slide = styled(Box)<{ active?: boolean }>`
  width: 100%;
  height: 100%;
  position: absolute;
  transition: all 0.4s ease;
  background-color: var(--colors-neutrals0);

  left: ${({ active }) => (active ? '0px' : '100%')};
`;

export default PhotoViewer;
